import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/lib/theme';
import { trackPageView } from '@/lib/pixel';
import AuthCallback from '@/components/AuthCallback';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import AdminLogin from '@/pages/AdminLogin';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import PendingApproval from '@/pages/PendingApproval';

import ArchitectLayout from '@/pages/architect/ArchitectLayout';
import Home from '@/pages/architect/Home';
import MyQR from '@/pages/architect/MyQR';
import Earnings from '@/pages/architect/Earnings';
import Rewards from '@/pages/architect/Rewards';
import Leaderboard from '@/pages/architect/Leaderboard';
import Invite from '@/pages/architect/Invite';
import Events from '@/pages/architect/Events';
import Profile from '@/pages/architect/Profile';

import AdminLayout from '@/pages/admin/AdminLayout';
import AdminOverview from '@/pages/admin/AdminOverview';
import AdminArchitects from '@/pages/admin/AdminArchitects';
import AdminSales from '@/pages/admin/AdminSales';
import AdminPayouts from '@/pages/admin/AdminPayouts';
import AdminParams from '@/pages/admin/AdminParams';
import AdminCampaigns from '@/pages/admin/AdminCampaigns';
import AdminReports from '@/pages/admin/AdminReports';
import AdminReferrals from '@/pages/admin/AdminReferrals';
import AdminRewards from '@/pages/admin/AdminRewards';
import AdminApi from '@/pages/admin/AdminApi';
import AdminAdmins from '@/pages/admin/AdminAdmins';

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-page">
    <div className="w-6 h-6 border-2 border-ink/30 border-t-ink rounded-full animate-spin" />
  </div>
);

const RequireRole = ({ roles, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/giris" replace />;
  if (roles && !roles.includes(user.role)) {
    const home = user.role === 'admin' ? '/admin' : '/app';
    return <Navigate to={home} replace />;
  }
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/giris" element={<Login mode="login" />} />
      <Route path="/kayit" element={<Login mode="register" />} />
      <Route path="/yonetici" element={<AdminLogin />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/dogrula" element={<VerifyEmail />} />
      <Route path="/onay-bekliyor" element={<PendingApproval />} />

      <Route path="/app" element={<RequireRole roles={['architect']}><ArchitectLayout /></RequireRole>}>
        <Route index element={<Home />} />
        <Route path="qr" element={<MyQR />} />
        <Route path="hakedis" element={<Earnings />} />
        <Route path="oduller" element={<Rewards />} />
        <Route path="siralama" element={<Leaderboard />} />
        <Route path="davet" element={<Invite />} />
        <Route path="etkinlikler" element={<Events />} />
        <Route path="profil" element={<Profile />} />
      </Route>

      <Route path="/admin" element={<RequireRole roles={['admin']}><AdminLayout /></RequireRole>}>
        <Route index element={<AdminOverview />} />
        <Route path="mimarlar" element={<AdminArchitects />} />
        <Route path="satislar" element={<AdminSales />} />
        <Route path="hakedis" element={<AdminPayouts />} />
        <Route path="parametreler" element={<AdminParams />} />
        <Route path="kampanyalar" element={<AdminCampaigns />} />
        <Route path="oduller" element={<AdminRewards />} />
        <Route path="davet" element={<AdminReferrals />} />
        <Route path="raporlar" element={<AdminReports />} />
        <Route path="pos" element={<AdminApi />} />
        <Route path="yoneticiler" element={<AdminAdmins />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PixelTracker() {
  const location = useLocation();
  const first = React.useRef(true);
  React.useEffect(() => {
    if (first.current) { first.current = false; return; } // initial PageView fired in index.html
    trackPageView();
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <PixelTracker />
            <AppRouter />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
